import { useUploadFile } from '@dealstack/frontend/shared/components/DragAndDrop/useUploadFile';
import {
	getFileInfo,
	getFileReactQuery,
	uploadFile,
} from '@dealstack/frontend/shared/dataExchange/services/god.service';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { downloadFileFromBlob } from '@dealstack/frontend/shared/files/downloadFile';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

export const useFiles = () => {
	const queryClient = useQueryClient();
	const [downloadFileId, setDownloadFileIdValue] = useState<null | string>(null);
	const [fileData, setFileData] = useState<null | {
		fileName: string;
		fileExtension: string | null;
		blob: Blob;
	}>(null);
	const uploadFileRequest = useWrappedRequest(uploadFile);
	const { percentComplete, isUploadSuccess } = useUploadFile({
		uploadFileRequest,
	});
	const fileInfoRequest = useQuery({
		queryKey: [
			formatUnicorn('/godmode/file/{fileId}/info', { fileId: downloadFileId ?? '' }),
			{ id: downloadFileId },
		],
		queryFn: () =>
			getFileInfo(
				formatUnicorn('/godmode/file/{fileId}/info', { fileId: downloadFileId ?? '' })
			),
		enabled: false,
	});

	const getFileQuery = useQuery({
		queryKey: [API_PATHS.GODMODE_FILE_ID_GET, { id: fileInfoRequest?.data?.payload?.id ?? '' }],
		queryFn: ({ signal }) =>
			getFileReactQuery(
				API_PATHS.GODMODE_FILE_ID_GET,
				{ id: fileInfoRequest?.data?.payload?.id ?? '' },
				signal
			),
		enabled: !!fileInfoRequest?.data?.payload?.id,
	});

	useEffect(() => {
		if (!getFileQuery.isSuccess) {
			return;
		}
		void (async () => {
			const response = getFileQuery.data;
			if (!response) {
				return;
			}
			const fileExtension = response.headers.get('Fileextension');
			const fileName = response.headers.get('Filename') as string;
			const blob = await response?.blob();
			setFileData({
				fileName,
				fileExtension,
				blob,
			});
		})();
	}, [getFileQuery.isSuccess, getFileQuery.data]);

	const onFindFile = async () => {
		setFileData(null);
		queryClient.removeQueries({
			queryKey: [
				API_PATHS.GODMODE_FILE_ID_GET,
				{ id: fileInfoRequest?.data?.payload?.id ?? '' },
			],
		});
		const response = await fileInfoRequest.refetch();
		if (response.isSuccess) {
			showToastV2({
				title: 'success',
				message: 'File found!',
			});
		}
	};
	const onDownload = () => {
		if (!fileData?.blob) {
			showToastV2({
				title: 'Error',
				message: 'No BLOB',
			});
			return;
		}
		downloadFileFromBlob(fileData?.blob, fileData?.fileName ?? 'admin-file');
	};
	useEffect(() => {
		if (!isUploadSuccess) {
			return;
		}
		showToastV2({
			title: 'success',
			message: 'Upload complete',
		});
	}, [isUploadSuccess]);

	const onDropFiles = async (files) => {
		for (const file of files) {
			const formData = new FormData();
			formData.append('file', file);
			formData.append('fileSize', file.size.toString());
			formData.append('skipPdfProcessing', 'false');

			await uploadFileRequest
				.fetch({
					formData,
				})
				.concludeWithPromise();
		}
	};

	return {
		downloadFileId,
		onDownload,
		onFindFile,
		fileData,
		setDownloadFileIdValue,
		uploadFileRequest,
		onDropFiles,
		isUploadSuccess,
		percentComplete,
	};
};
